import { useState, useEffect, useRef } from "react";

const IMAGE_NUMBERS = [1, 2, 3, 4, 5, 6, 7];
const VISIBLE_DURATION = 3000; // Time each image stays fully visible (ms)
const FADE_DURATION = 1200;   // Crossfade duration (ms)

const getImagePath = (imageNumber, isMobile = false) =>
  isMobile ? `/home/mnil${imageNumber}.webp` : `/home/nil${imageNumber}.webp`;

const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};

export default function InfluencerSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [displayNextIndex, setDisplayNextIndex] = useState(1); // Only updated when we START a fade (prevents glitch)
  const currentIndexRef = useRef(0);
  const timeout1Ref = useRef(null);
  const timeout2Ref = useRef(null);
  const mountedRef = useRef(true);

  currentIndexRef.current = currentIndex;
  const currentNum = IMAGE_NUMBERS[currentIndex];
  const nextNum = IMAGE_NUMBERS[displayNextIndex];

  // Preload next image
  useEffect(() => {
    preloadImage(getImagePath(nextNum, true));
    preloadImage(getImagePath(nextNum, false));
  }, [nextNum]);

  useEffect(() => {
    mountedRef.current = true;

    const scheduleNextTransition = () => {
      if (!mountedRef.current) return;

      timeout1Ref.current = setTimeout(() => {
        if (!mountedRef.current) return;

        const nextIdx = (currentIndexRef.current + 1) % IMAGE_NUMBERS.length;
        setDisplayNextIndex(nextIdx); // Update next image BEFORE starting fade

        setIsFading(true);

        timeout2Ref.current = setTimeout(() => {
          if (!mountedRef.current) return;
          currentIndexRef.current = nextIdx;
          setCurrentIndex(nextIdx);
          setIsFading(false);
          scheduleNextTransition();
        }, FADE_DURATION);
      }, VISIBLE_DURATION);
    };

    scheduleNextTransition();

    return () => {
      mountedRef.current = false;
      if (timeout1Ref.current) clearTimeout(timeout1Ref.current);
      if (timeout2Ref.current) clearTimeout(timeout2Ref.current);
    };
  }, []);

  const currentMobileImage = getImagePath(currentNum, true);
  const currentDesktopImage = getImagePath(currentNum, false);
  const nextMobileImage = getImagePath(nextNum, true);
  const nextDesktopImage = getImagePath(nextNum, false);

  const transitionStyle = { transition: `opacity ${FADE_DURATION}ms ease-in-out`, willChange: "opacity" };

  return (
    <div
      className="w-full text-white font-fira-sans relative overflow-hidden bg-[#08090A]"
      style={{
        borderTop: "1px solid rgba(255,255,255,0.2)",
     
      }}
    >
      <style>{`
        .influencer-bg-current-mobile {
          background-image: url('${currentMobileImage}');
        }
        .influencer-bg-next-mobile {
          background-image: url('${nextMobileImage}');
        }
        .influencer-bg-current-desktop {
          background-image: url('${currentDesktopImage}');
        }
        .influencer-bg-next-desktop {
          background-image: url('${nextDesktopImage}');
        }
      `}</style>
      
      {/* Background layers on main div - Mobile only */}
      <div
        className="md:hidden absolute inset-0 influencer-bg-current-mobile"
        style={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: isFading ? 0 : 1,
          zIndex: 1,
          ...transitionStyle,
        }}
      />
      <div
        className="md:hidden absolute inset-0 influencer-bg-next-mobile"
        style={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: isFading ? 1 : 0,
          zIndex: 2,
          ...transitionStyle,
        }}
      />

      {/* Framed container */}
      <div
        className="relative z-10 gutter-x md:overflow-hidden"
        style={{
          borderLeft: "1px solid rgba(255,255,255,0.2)",
          borderRight: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        {/* Background layers inside framed container - Desktop only */}
        <div
          className="hidden md:block absolute inset-0 influencer-bg-current-desktop"
          style={{
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: isFading ? 0 : 1,
            zIndex: 1,
            ...transitionStyle,
          }}
        />
        <div
          className="hidden md:block absolute inset-0 influencer-bg-next-desktop"
          style={{
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: isFading ? 1 : 0,
            zIndex: 2,
            ...transitionStyle,
          }}
        />
        {/* Content */}
        <div className="relative z-10 py-10  min-h-[750px] md:min-h-[450px]">
          <div className="relative md:pl-[60px] md:pr-[60px] px-4 pt-8 md:pt-12">
            <p className="text-[16px] tracking-[1.75px] leading-[160%] font-normal uppercase text-white/60 mb-4">
              GLOBALLY ACCLAIMED
            </p>
            <h2 className="text-[30px] md:text-[38px] leading-[120%] font-medium text-white max-w-[400px]">
              Recommended by your Favourite Influencers and Creators
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
